import { useState, useEffect, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useNotifications } from '@context/NotificationContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { getSocket } from '@utils/socket';
import api from '@utils/api';
import Avatar from '@components/ui/Avatar';
import { formatRelativeTime } from '@utils/helpers';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';

const SOCKET_EVENTS = {
  JOIN_ROOM: 'join_room',
  LEAVE_ROOM: 'leave_room',
  SEND_MESSAGE: 'send_message',
  RECEIVE_MESSAGE: 'receive_message',
  TYPING: 'typing',
  STOP_TYPING: 'stop_typing',
};

const ChatBox = ({ activityId, readOnly = false }) => {
  const { user, token } = useAuth();
  const { refreshUnreadCount } = useNotifications();
  const { ready: publicConfigReady, siteOrigin } = usePublicConfig();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const scrollContainerRef = useRef(null);
  const typingTimeout = useRef(null);

  /** Scroll only the chat pane — never `scrollIntoView` (it scrolls the window too). */
  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${activityId}`);
      setMessages(data.messages);
    } catch { /* empty */ } finally {
      setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    fetchMessages();
    if (!token || !publicConfigReady) return undefined;
    const socket = getSocket(token);
    if (!socket) return undefined;

    socket.emit(SOCKET_EVENTS.JOIN_ROOM, activityId);

    socket.on(SOCKET_EVENTS.RECEIVE_MESSAGE, (message) => {
      setMessages((prev) => [...prev, message]);
    });

    socket.on(SOCKET_EVENTS.TYPING, ({ user: u }) => {
      setTypingUsers((prev) => prev.find((x) => x.id === u.id) ? prev : [...prev, u]);
    });

    socket.on(SOCKET_EVENTS.STOP_TYPING, ({ user: u }) => {
      setTypingUsers((prev) => prev.filter((x) => x.id !== u.id));
    });

    return () => {
      socket.emit(SOCKET_EVENTS.LEAVE_ROOM, activityId);
      socket.off(SOCKET_EVENTS.RECEIVE_MESSAGE);
      socket.off(SOCKET_EVENTS.TYPING);
      socket.off(SOCKET_EVENTS.STOP_TYPING);
    };
  }, [activityId, token, publicConfigReady, fetchMessages]);

  /** Message unread badge counts `Notification` rows; opening chat here (not only via inbox) must mark read and refresh the nav badge. */
  useEffect(() => {
    if (readOnly || !activityId || !user || loading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await api.patch(`/messages/read/${activityId}`);
        if (!cancelled) await refreshUnreadCount();
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityId, readOnly, user, loading, refreshUnreadCount]);

  useEffect(() => {
    if (loading) return;
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const handleSend = (e) => {
    e.preventDefault();
    if (readOnly || !text.trim()) return;
    const socket = getSocket(token);
    if (socket) {
      socket.emit(SOCKET_EVENTS.SEND_MESSAGE, { activityId, text: text.trim() });
      socket.emit(SOCKET_EVENTS.STOP_TYPING, activityId);
    }
    setText('');
  };

  const handleTyping = () => {
    if (readOnly) return;
    const socket = getSocket(token);
    if (!socket) return;
    socket.emit(SOCKET_EVENTS.TYPING, activityId);
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit(SOCKET_EVENTS.STOP_TYPING, activityId);
    }, 2000);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8 text-sm text-surface-400">Loading chat...</div>;
  }

  return (
    <div className="flex flex-col rounded-2xl border border-surface-200/60 bg-white overflow-hidden dark:border-surface-800 dark:bg-surface-900/80">
      <div className="border-b border-surface-100 px-4 py-3 dark:border-surface-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">Messages</h3>
      </div>

      <div
        ref={scrollContainerRef}
        className="h-72 overflow-y-auto p-4 scrollbar-hide bg-surface-50/50 dark:bg-surface-950/30"
      >
        {messages.length === 0 && (
          <p className="text-center text-sm text-surface-400 py-10">
            {readOnly ? 'No messages in this chat.' : 'No messages yet. Say hello!'}
          </p>
        )}
        {messages.map((msg) => {
          const isOwn = msg.sender?._id === user?._id || msg.sender?.id === user?._id;
          return (
            <div key={msg._id} className={`mb-3 flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {!isOwn && (
                <Avatar
                  name={msg.sender?.name}
                  src={resolveProfileDisplaySrc(msg.sender, siteOrigin)}
                  size="sm"
                  className="mr-2 mt-1"
                />
              )}
              <div className={`max-w-[75%] ${isOwn ? 'text-right' : ''}`}>
                {!isOwn && <p className="mb-0.5 text-[10px] font-semibold text-surface-400">{msg.sender?.name}</p>}
                <div className={`inline-block rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  isOwn
                    ? 'bg-gradient-to-r from-brand-500 to-brand-600 text-white rounded-br-lg shadow-sm'
                    : 'bg-white text-surface-800 border border-surface-200/60 dark:bg-surface-800 dark:text-surface-200 dark:border-surface-700 rounded-bl-lg'
                }`}>
                  {msg.text}
                </div>
                <p className="mt-1 text-[10px] text-surface-400">{formatRelativeTime(msg.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {typingUsers.length > 0 && (
        <div className="px-4 py-1 text-xs text-surface-400 border-t border-surface-100 dark:border-surface-800">
          {typingUsers.map((u) => u.name).join(', ')} typing...
        </div>
      )}

      <form
        onSubmit={handleSend}
        className={`flex flex-col gap-2 border-t border-surface-100 p-3 dark:border-surface-800 ${
          readOnly ? 'bg-surface-100/80 dark:bg-surface-900' : 'bg-white dark:bg-surface-900/80'
        }`}
      >
        {readOnly && (
          <p className="text-center text-xs text-surface-500 dark:text-surface-400">
            This vibe has ended - you can&apos;t send messages now.
          </p>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              handleTyping();
            }}
            placeholder={readOnly ? 'Chat is read-only' : 'Type a message...'}
            disabled={readOnly}
            className="input-field flex-1 !py-2.5 !rounded-2xl !bg-surface-50 dark:!bg-surface-800 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={readOnly || !text.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-md shadow-brand-500/20 transition-all duration-300 hover:shadow-lg disabled:opacity-30 disabled:shadow-none"
          >
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatBox;
